/*
    Made by : GitHub Copilot
    Date : April 23, 2025
 */

// ----------------------------------------------------------------------------- //
// IMPORTS //

import { useState, useContext, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ThemeContext } from "../../App";
import styles from "./login.module.css";

// ----------------------------------------------------------------------------- //
// COMPONENT //

function Login() {
    const { isDarkMode } = useContext(ThemeContext);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        username: "",
        password: "",
        rememberMe: false,
    });

    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    // Check if the user is already logged in when the component mounts
    const checkUserLoggedIn = async () => {
        try {
            // With cookies, we don't need to manually attach the token
            const verifyResponse = await fetch("/auth/verify", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
                // This ensures cookies are sent with the request
                credentials: "include",
            });

            if (verifyResponse.ok) {
                navigate("/admin"); // Redirect to admin page if already logged in
            }
        } catch (error) {
            console.error("Auth verification error:", error);
        }
    };

    useEffect(() => {
        checkUserLoggedIn();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError("");
        setIsLoading(true);

        try {
            const response = await fetch("/auth/login", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    username: formData.username,
                    password: formData.password,
                }),
                // This ensures cookies are set
                credentials: "include",
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || "Login failed");
            }

            // The token is now stored in cookies, so we only need to store user info
            localStorage.setItem("user", JSON.stringify(data.user));

            // Redirect to admin page after successful login
            navigate("/admin");
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : "Login falhou. Por favor verifique suas credenciais.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={`${styles.loginPage} ${isDarkMode ? styles.darkMode : styles.lightMode}`}>
            <div className={styles.loginContainer}>
                <div className={styles.logoContainer}>
                    <img src="/BotPombal.png" alt="Bot Pombal Logo" className={styles.logo} />
                </div>

                {error && <div className={styles.errorMessage}>{error}</div>}

                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGroup}>
                        <label htmlFor="username">Username</label>
                        <input
                            type="text"
                            id="username"
                            name="username"
                            value={formData.username}
                            onChange={handleChange}
                            placeholder="Username"
                            required
                            className={styles.input}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="password">Senha</label>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Password"
                            required
                            className={styles.input}
                        />
                    </div>

                    <button type="submit" className={styles.loginButton} disabled={isLoading}>
                        {isLoading ? "Entrando..." : "Entrar"}
                    </button>
                </form>

                <div className={styles.backToHomeLink}>
                    <Link to="/">Voltar para a página inicial</Link>
                </div>
            </div>
        </div>
    );
}

// ----------------------------------------------------------------------------- //
// EXPORTS //

export default Login;

// ----------------------------------------------------------------------------- //
